// src/components/vendor/VendorSupportPage.jsx - COMPLETE
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  CardActionArea,
  Stack,
} from '@mui/material';
import {
  Add,
  Email,
  Chat,
  Help,
  ConfirmationNumber,
  CheckCircle,
  Schedule,
  Close,
  Refresh,
  Reply,
  Visibility,
  Send,
  Phone,
  AccessTime,
  Check,
  Storefront,
  Person,
  Timeline,
  HourglassEmpty,
  Pending,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Shared components and constants
import { supportApi } from '../../api/supportApi';
import TicketList from '../Support/TicketList';
import StatusBadge from '../Support/StatusBadge';
import StatusFlow from '../Support/StatusFlow';
import { SUPPORT_STATUSES, SUPPORT_CATEGORIES, PRIORITY_LEVELS, getCategoryLabel, getCategoryIcon } from '../../constants/supportConstants';

// ============================================================
// HELPER FUNCTIONS
// ============================================================
const getStatusConfig = (status) => {
  const found = SUPPORT_STATUSES.find((s) => s.value === status);
  return found || SUPPORT_STATUSES[0];
};

const getStatusBgColor = (status) => {
  const config = getStatusConfig(status);
  const colorMap = {
    info: 'info',
    warning: 'warning',
    secondary: 'secondary',
    success: 'success',
    default: 'default',
  };
  return colorMap[config.color] || 'default';
};

const getPriorityBgColor = (priority) => {
  const config = PRIORITY_LEVELS.find((p) => p.value === priority);
  const colorMap = {
    info: 'info',
    warning: 'warning',
    error: 'error',
  };
  return colorMap[config.color] || 'default';
};

const getStatusLabel = (status) => {
  const config = getStatusConfig(status);
  return config.label;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const VendorSupportPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    waitingSeller: 0,
    waitingSupport: 0,
    resolved: 0,
    closed: 0,
  });
  const [faqs, setFaqs] = useState({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [openTicketDetail, setOpenTicketDetail] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    message: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ============================================================
  // FETCH DATA
  // ============================================================

  const fetchTickets = async () => {
    try {
      const response = await supportApi.getVendorTickets();
      const ticketsData = response.tickets || [];
      setTickets(ticketsData);

      const calculatedStats = {
        total: ticketsData.length,
        open: ticketsData.filter((t) => t.status === 'open').length,
        inProgress: ticketsData.filter((t) => t.status === 'in-progress').length,
        waitingSeller: ticketsData.filter((t) => t.status === 'waiting-for-seller').length,
        waitingSupport: ticketsData.filter((t) => t.status === 'waiting-for-support').length,
        resolved: ticketsData.filter((t) => t.status === 'resolved').length,
        closed: ticketsData.filter((t) => t.status === 'closed').length,
      };
      setStats(calculatedStats);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      showSnackbar('Failed to fetch tickets', 'error');
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await supportApi.getVendorFaqs();
      setFaqs(response.faqs || {});
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTickets(), fetchFaqs()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // ============================================================
  // HANDLERS
  // ============================================================

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      showSnackbar('Subject and message are required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await supportApi.createTicket(newTicket);
      showSnackbar('Ticket created successfully');
      setOpenTicketDialog(false);
      setNewTicket({
        subject: '',
        category: 'general',
        priority: 'medium',
        message: '',
      });
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      showSnackbar('Failed to create ticket', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyTicket = async () => {
    if (!replyMessage.trim()) {
      showSnackbar('Reply message is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await supportApi.replyToTicket(selectedTicket.id, { message: replyMessage });
      showSnackbar('Reply sent successfully');
      setReplyMessage('');
      fetchTickets();

      const response = await supportApi.getVendorTicket(selectedTicket.id);
      setSelectedTicket(response.ticket);
    } catch (error) {
      console.error('Error sending reply:', error);
      showSnackbar('Failed to send reply', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;

    setSubmitting(true);
    try {
      await supportApi.closeTicket(ticketId);
      showSnackbar('Ticket closed successfully');
      fetchTickets();
      if (selectedTicket) {
        setSelectedTicket({ ...selectedTicket, status: 'closed' });
      }
    } catch (error) {
      console.error('Error closing ticket:', error);
      showSnackbar('Failed to close ticket', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  const renderStatCard = (label, value, color, icon) => (
    <Grid item xs={6} sm={3}>
      <Card
        sx={{
          height: '100%',
          borderLeft: `4px solid ${color}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {icon && <Box sx={{ color }}>{icon}</Box>}
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ color }}>
              {value || 0}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderFaqs = () => {
    if (Object.keys(faqs).length === 0) {
      return (
        <Paper sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center', borderRadius: 2 }}>
          <Help sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No FAQs Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for frequently asked questions.
          </Typography>
        </Paper>
      );
    }

    // Sort categories
    const sortedCategories = Object.keys(faqs).sort();

    return sortedCategories.map((category) => (
      <Box key={category} sx={{ mb: 4 }}>
        <Typography
          variant="subtitle1"
          sx={{
            textTransform: 'capitalize',
            mb: 2,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <span style={{ fontSize: 20 }}>{getCategoryIcon(category)}</span>
          {getCategoryLabel(category)}
          <Chip
            label={`${faqs[category].length} questions`}
            size="small"
            sx={{ ml: 1 }}
          />
        </Typography>
        {faqs[category].map((faq) => (
          <Card key={faq.id} sx={{ mb: 1.5, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold">
                {faq.question}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                {faq.answer}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    ));
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, pb: { xs: 10, sm: 12 } }}>
      {/* Header */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
            Support Center
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Get help and support for your vendor account
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenTicketDialog(true)}
          size={isMobile ? 'small' : 'medium'}
          fullWidth={isMobile}
        >
          New Ticket
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={isMobile ? 1 : 3} sx={{ mb: 4 }}>
        {renderStatCard('Total', stats.total, theme.palette.primary.main, <ConfirmationNumber fontSize="small" />)}
        {renderStatCard('Open', stats.open, '#1976d2', <Email fontSize="small" />)}
        {renderStatCard('In Progress', stats.inProgress, '#ed6c02', <Schedule fontSize="small" />)}
        {renderStatCard('Waiting Seller', stats.waitingSeller, '#ed6c02', <HourglassEmpty fontSize="small" />)}
        {renderStatCard('Waiting Support', stats.waitingSupport, '#9c27b0', <Pending fontSize="small" />)}
        {renderStatCard('Resolved', stats.resolved, '#2e7d32', <CheckCircle fontSize="small" />)}
        {renderStatCard('Closed', stats.closed, '#9e9e9e', <Close fontSize="small" />)}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={selectedTab}
          onChange={(e, v) => setSelectedTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? 'fullWidth' : 'standard'}
          centered={!isMobile}
          sx={{
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 56 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
          }}
        >
          <Tab icon={<ConfirmationNumber />} label={isMobile ? 'Tickets' : 'My Tickets'} />
          <Tab icon={<Help />} label={isMobile ? 'FAQs' : 'FAQs'} />
          <Tab icon={<Chat />} label={isMobile ? 'Chat' : 'Live Chat'} />
        </Tabs>
      </Paper>

      {/* ============================================================ */}
      {/* TAB 0: TICKETS */}
      {/* ============================================================ */}
      {selectedTab === 0 && (
        <Box>
          {tickets.length === 0 ? (
            <Paper sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center', borderRadius: 2 }}>
              <ConfirmationNumber sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Tickets Found
              </Typography>
              <Typography variant="body2" paragraph color="text.secondary">
                You haven't created any support tickets yet.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenTicketDialog(true)}
              >
                Create New Ticket
              </Button>
            </Paper>
          ) : (
            <TicketList
			  tickets={tickets}
			  onViewTicket={(ticket) => {
				setSelectedTicket(ticket);
				setOpenTicketDetail(true);
			  }}
			  onCloseTicket={handleCloseTicket}
			  loading={loading}
			  isVendor={true}
			  showVendor={false}
			/>
          )}
        </Box>
      )}

      {/* ============================================================ */}
      {/* TAB 1: FAQS */}
      {/* ============================================================ */}
      {selectedTab === 1 && (
        <Box>
          {renderFaqs()}
        </Box>
      )}

      {/* ============================================================ */}
      {/* TAB 2: LIVE CHAT */}
      {/* ============================================================ */}
      {selectedTab === 2 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
            <Chat sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Live Chat Support
            </Typography>
            <Typography variant="body2" paragraph color="text.secondary">
              Chat with our support team for immediate assistance.
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              gap={{ xs: 2, sm: 4 }}
              flexWrap="wrap"
              sx={{ mb: 3 }}
            >
              <Box textAlign="center">
                <AccessTime sx={{ color: 'primary.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  Avg. Wait Time
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  &lt; 1 min
                </Typography>
              </Box>
              <Box textAlign="center">
                <Check sx={{ color: 'success.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  Availability
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  24/7
                </Typography>
              </Box>
              <Box textAlign="center">
                <Phone sx={{ color: 'info.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  Support
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  Live Agents
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<Chat />}
              onClick={() => window.open('/vendor/chat', '_blank')}
              fullWidth={isMobile}
            >
              Start Live Chat
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ============================================================ */}
      {/* DIALOG: CREATE TICKET */}
      {/* ============================================================ */}
      <Dialog
        open={openTicketDialog}
        onClose={() => setOpenTicketDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Create Support Ticket</Typography>
          <Typography variant="caption" color="text.secondary">
            Describe your issue and we'll get back to you
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Subject"
            value={newTicket.subject}
            onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={newTicket.category}
              label="Category"
              onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
            >
              {SUPPORT_CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={newTicket.priority}
              label="Priority"
              onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Message"
            multiline
            rows={4}
            value={newTicket.message}
            onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenTicketDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTicket}
            disabled={submitting}
            fullWidth={isMobile}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* DIALOG: TICKET DETAIL */}
      {/* ============================================================ */}
      <Dialog
        open={openTicketDetail}
        onClose={() => setOpenTicketDetail(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={1}
          >
            <Box>
              <Typography variant="h6">Ticket Details</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTicket?.ticketNumber || `#${selectedTicket?.id}`}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpenTicketDetail(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <>
              {/* Ticket Details */}
              <Box mb={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedTicket.subject}
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <StatusBadge status={selectedTicket.status} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    label={selectedTicket.priority}
                    color={getPriorityBgColor(selectedTicket.priority)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" textTransform="capitalize">
                    {getCategoryLabel(selectedTicket.category)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedTicket.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Message
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mt: 0.5 }}>
                    <Typography variant="body2">{selectedTicket.message}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Status Flow */}
              <StatusFlow
                currentStatus={selectedTicket.status}
                resolvedAt={selectedTicket.resolvedAt}
                closedAt={selectedTicket.closedAt}
              />

              {/* Replies */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Replies ({selectedTicket.SupportReplies?.length || 0})
              </Typography>
              {selectedTicket.SupportReplies && selectedTicket.SupportReplies.length > 0 ? (
                <List>
                  {selectedTicket.SupportReplies.map((reply) => (
                    <Paper
                      key={reply.id}
                      sx={{
                        mb: 1,
                        p: 2,
                        bgcolor: reply.isAdmin ? 'grey.50' : 'background.paper',
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: reply.isAdmin ? 'primary.main' : 'secondary.main',
                            }}
                          >
                            {reply.isAdmin ? 'A' : 'V'}
                          </Avatar>
                          <Typography variant="subtitle2">
                            {reply.isAdmin ? 'Support Team' : 'You'}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(reply.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {reply.message}
                      </Typography>
                    </Paper>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" align="center" py={2}>
                  No replies yet
                </Typography>
              )}

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Add Reply
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your reply..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      endIcon={<Send />}
                      onClick={handleReplyTicket}
                      disabled={!replyMessage.trim() || submitting}
                    >
                      {submitting ? <CircularProgress size={24} /> : 'Send Reply'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={() => handleCloseTicket(selectedTicket.id)}
                      disabled={submitting}
                    >
                      Close Ticket
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTicketDetail(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* SNACKBAR */}
      {/* ============================================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default VendorSupportPage;